import React, { useMemo, useCallback, useState } from 'react';

import { PaginatedEntityTable } from 'components/common';
import type { Sort } from 'stores/PaginationTypes';
import type { Collection, Entity } from 'collections/types';
import { fetchPaginatedCollectionCandidates, keyFn } from 'collections/hooks/useCollectionCandidates';

import EntityActions from './EntityActions';
import customColumnRenderers from './customColumnRenderers';
import BulkActions from './BulkActions';

export const DEFAULT_LAYOUT = {
  entityTableId: 'entities',
  defaultPageSize: 20,
  defaultSort: { attributeId: 'name', direction: 'asc' } as Sort,
  defaultDisplayedAttributes: ['name', 'type'],
};

const COLUMNS_ORDER = ['name', 'type'];

type Props = {
  collectionId: Collection['id'];
};

const EntitiesList = ({ collectionId }: Props) => {
  const [selectedEntities, setSelectedEntities] = useState<Array<Entity>>([]);

  const onBulkSelect = (selected: Array<string>, data: Array<Entity>) => {
    setSelectedEntities(data.filter((entity) => selected.includes(entity.id)));
  };

  const bulkSelection = useMemo(
    () => ({
      onChangeSelection: onBulkSelect,
      actions: <BulkActions selectedEntities={selectedEntities} collectionId={collectionId} />,
    }),
    [collectionId, selectedEntities],
  );

  const entitiesActions = useCallback(
    ({ id, type }: Entity) => <EntityActions collectionId={collectionId} id={id} type={type} />,
    [collectionId],
  );

  return (
    <PaginatedEntityTable<Entity>
      humanName="Entities"
      columnsOrder={COLUMNS_ORDER}
      entityActions={entitiesActions}
      tableLayout={DEFAULT_LAYOUT}
      fetchEntities={(searchParams) => fetchPaginatedCollectionCandidates(collectionId, searchParams)}
      keyFn={(searchParams) => keyFn(collectionId, searchParams)}
      columnRenderers={customColumnRenderers}
      entityAttributesAreCamelCase={false}
      searchPlaceholder="Search for entity"
      bulkSelection={bulkSelection}
    />
  );
};

export default EntitiesList;
